import { useMemo, useState } from "react";
import { Clock, Check, Sparkles } from "lucide-react";
import { cleanName, splitName } from "../../lib/serviceMenu";

// Laser Hair Removal: pick the body area first → single sessions for that area + plans/packages.
const AREAS = [
  { key: "face", en: "Face & Neck", fr: "Visage & cou", rx: /upper lip|\blip\b|chin|sideburn|full face|\bneck\b|areola|\bface\b/i },
  { key: "arms", en: "Arms & Underarms", fr: "Bras & aisselles", rx: /underarm|half arms|full arms|\barms?\b|hands?|fingers/i },
  { key: "torso", en: "Chest, Back & Stomach", fr: "Torse, dos & ventre", rx: /chest|abdomen|stomach|\bback\b|shoulders|linea|nipple/i },
  { key: "bikini", en: "Bikini & Brazilian", fr: "Bikini & brésilien", rx: /bikini|brazilian/i },
  { key: "legs", en: "Legs & Derrière", fr: "Jambes & fessier", rx: /legs?\b|derri|buttock|feet|toes/i },
];
const PLAN_RX = /head to toe|unlimited|6 month|per month|\/mo/i;
const PACKAGE_RX = /package|full body/i;

const best = (list) => [...list].sort((a, b) => (b.team_member_ids?.length || 0) - (a.team_member_ids?.length || 0))[0];
const dedupe = (list) => {
  const by = {};
  for (const s of list) { const k = cleanName(s.name).replace(/^laser( hair removal)?\s*[—·-]\s*/i, "").toLowerCase().trim(); (by[k] ||= []).push(s); }
  return Object.values(by).map(best).sort((a, b) => (parseInt(String(a.price).replace(/\D/g, ""), 10) || 0) - (parseInt(String(b.price).replace(/\D/g, ""), 10) || 0));
};
const areaLabel = (s) => cleanName(s.name).replace(/^laser( hair removal)?\s*[—·-]\s*/i, "");

export default function LaserAreaPicker({ services, isSelected, onToggle, codeOf, isFr }) {
  const [area, setArea] = useState(null);
  const { plans, packages, singles } = useMemo(() => {
    const plans = services.filter((s) => PLAN_RX.test(s.name || ""));
    const packages = services.filter((s) => !PLAN_RX.test(s.name || "") && PACKAGE_RX.test(s.name || ""));
    const singles = services.filter((s) => !PLAN_RX.test(s.name || "") && !PACKAGE_RX.test(s.name || ""));
    return { plans, packages, singles };
  }, [services]);
  const forArea = (a) => dedupe(singles.filter((s) => a.rx.test(areaLabel(s))));
  const current = area ? AREAS.find((a) => a.key === area) : null;
  const rows = current ? forArea(current) : area === "packages" ? dedupe(packages) : [];

  const Row = ({ s, big }) => {
    const sel = isSelected(s);
    const code = codeOf?.(s);
    return (
      <button type="button" onClick={() => onToggle(s)}
        className={`w-full text-left rounded-lg border px-3 py-2.5 transition ${sel ? "border-amber-400 bg-amber-500/15" : big ? "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20" : "border-zinc-700/60 bg-zinc-800/40 hover:bg-zinc-800"}`}
        data-testid={`select-service-${s.variation_id}`}>
        <div className="flex items-center gap-2">
          {code && <span className="shrink-0 px-1.5 py-0.5 rounded bg-amber-300 text-zinc-900 text-[12px] font-black tracking-wider ring-1 ring-amber-500/40">#{code}</span>}
          <span className={`flex-1 text-sm font-medium ${sel ? "text-amber-200" : "text-white"}`}>{big ? splitName(s.name).title : areaLabel(s)}</span>
          <span className="shrink-0 text-right">
            <span className="block text-xs font-bold text-amber-300">{s.price}{big && PLAN_RX.test(s.name) ? (isFr ? " / mois" : " / month") : ""}</span>
            <span className="block text-[10px] text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_minutes} min</span>
          </span>
          {sel && <Check className="w-4 h-4 text-amber-300" />}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="laser-area-picker">
      {plans.length > 0 && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-1.5 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{isFr ? "Forfait illimité" : "Unlimited plan"}</p>
          <div className="space-y-1.5">{dedupe(plans).map((s) => <Row key={s.variation_id} s={s} big />)}</div>
        </div>
      )}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1.5">{isFr ? "Ou choisissez une zone" : "Or pick a body area"}</p>
        <div className="grid grid-cols-2 gap-2">
          {AREAS.map((a) => { const n = forArea(a).length; return n > 0 && (
            <button key={a.key} type="button" onClick={() => setArea(area === a.key ? null : a.key)}
              className={`text-left rounded-xl border p-3 transition ${area === a.key ? "border-amber-400 bg-amber-500/15" : "border-zinc-700/70 bg-zinc-800/50 hover:border-amber-400"}`}
              data-testid={`laser-area-${a.key}`}>
              <span className="block text-sm font-bold text-white">{isFr ? a.fr : a.en}</span>
              <span className="block text-[10px] text-amber-300 mt-1 font-bold">{n} {isFr ? "zones" : "areas"}</span>
            </button>
          ); })}
          {packages.length > 0 && (
            <button type="button" onClick={() => setArea(area === "packages" ? null : "packages")}
              className={`text-left rounded-xl border p-3 transition ${area === "packages" ? "border-amber-400 bg-amber-500/15" : "border-zinc-700/70 bg-zinc-800/50 hover:border-amber-400"}`}
              data-testid="laser-area-packages">
              <span className="block text-sm font-bold text-white">{isFr ? "Forfaits multi-zones" : "Multi-area packages"}</span>
              <span className="block text-[10px] text-amber-300 mt-1 font-bold">{dedupe(packages).length} {isFr ? "forfaits" : "packages"}</span>
            </button>
          )}
        </div>
      </div>
      {area && (
        <div className="space-y-1.5" data-testid="laser-area-rows">
          <p className="text-xs text-zinc-400">{current ? (isFr ? current.fr : current.en) : (isFr ? "Forfaits" : "Packages")} · {isFr ? "séance unique, prix par visite" : "single session · price per visit"}</p>
          {rows.map((s) => <Row key={s.variation_id} s={s} />)}
        </div>
      )}
    </div>
  );
}
